import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, Cookie, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { trackCookieConsent } from "@/utils/analytics";
import { getGeoInfo } from "@/lib/geoConsent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
}

export const CookieConsent = () => {
  const brand = useBrand();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isStrictRegion, setIsStrictRegion] = useState(true); // default safe
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true, // Always required
    analytics: false,
    advertising: false,
  });

  useEffect(() => {
    // When the headless screenshot capture script loads pages with
    // ?og_capture=1, skip the consent banner so the screenshot shows the
    // actual feature page, not the cookie overlay. No analytics fired —
    // we don't want screenshot bots polluting the metric stream.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('og_capture') === '1') {
      setShowBanner(false);
      return;
    }
    const saved = localStorage.getItem("cookie-consent");
    if (saved) {
      const savedPreferences = JSON.parse(saved);
      setPreferences(savedPreferences);
      initializeTracking(savedPreferences);
      // AdSense script loaded on-demand by AdSlot.tsx when ads render
      return;
    }

    // No prior choice — decide flow based on visitor region
    let cancelled = false;
    getGeoInfo().then(({ isStrict }) => {
      if (cancelled) return;
      setIsStrictRegion(isStrict);

      if (isStrict) {
        // EU/EEA/UK/CH — strict opt-in. Show our consent banner.
        // AdSense script loaded on-demand by AdSlot.tsx (not here) to avoid
        // Google's Funding Choices CMP appearing before our own banner.
        setTimeout(() => setShowBanner(true), 1000);
      } else {
        // Notice model — auto-grant analytics + advertising with opt-out.
        // US/non-GDPR: implied consent for both; user can opt out via Manage.
        // Honor Global Privacy Control (CCPA/CPRA requirement)
        const gpcEnabled = !!(navigator as any).globalPrivacyControl;
        const noticePrefs: ConsentPreferences = {
          necessary: true,
          analytics: true,
          advertising: !gpcEnabled, // GPC = user opted out of ad personalization
        };
        setPreferences(noticePrefs);
        initializeTracking(noticePrefs);
        // Persist so we don't re-show on every page load
        localStorage.setItem("cookie-consent", JSON.stringify(noticePrefs));
        // Surface a brief notice (user can still customize via privacy page)
        setTimeout(() => setShowBanner(true), 1000);
        trackCookieConsent('notice_implied');
      }
    });
    return () => { cancelled = true; };
  }, []);

  const initializeTracking = (prefs: ConsentPreferences) => {
    // Initialize Google Analytics if analytics consent is given
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }

    // Initialize Google AdSense if advertising consent is given
    // Note: AdSense script itself is loaded on-demand by AdSlot.tsx when an ad
    // actually renders — loading it eagerly here triggers Google's Funding Choices
    // CMP dialog prematurely (before user even sees our cookie notice).
    if (prefs.advertising && window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  /** Dynamically load AdSense script (only after ad consent or for EU CMP flow) */
  const loadAdSenseScript = () => {
    const CLIENT_ID = 'ca-pub-9512047279819133';
    if (document.querySelector(`script[src*="${CLIENT_ID}"]`)) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT_ID}`;
    s.crossOrigin = 'anonymous';
    document.head.appendChild(s);
  };

  const saveConsent = (prefs: ConsentPreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify(prefs));
    setPreferences(prefs);
    initializeTracking(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    saveConsent(allAccepted);
    trackCookieConsent('accept_all');
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      advertising: false,
    };
    saveConsent(necessaryOnly);
    trackCookieConsent('necessary_only');
  };

  const saveCustomPreferences = () => {
    saveConsent(preferences);
    trackCookieConsent('custom');
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div
        className="fixed right-4 z-[10001] max-w-md"
        style={{ bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}
      >
        <Card className="border-matrix-green/50 backdrop-blur-sm shadow-2xl" style={{ background: '#000000c4' }}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <Cookie className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: 'rgba(255,255,255,0.6)' }} />
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#fff' }}>
                    {isStrictRegion
                      ? (brand.key === 'antigreed' ? 'Your Privacy Matters' : 'We Value Your Privacy')
                      : 'Cookie Notice'}
                  </h3>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {isStrictRegion ? (
                      brand.key === 'antigreed'
                        ? 'AntiGreed.app uses cookies to fight corporate greed more effectively. We track site usage to expose healthcare manipulation patterns and improve our accountability platform.'
                        : 'We use cookies and similar technologies to improve your experience, analyze site traffic, and serve personalized content and advertisements.'
                    ) : (
                      'We use cookies to analyze site usage and improve your experience. By continuing to browse, you agree to our use of cookies.'
                    )}
                    {' '}For more details, see our{" "}
                    <Link to="/privacy" className="hover:underline" style={{ color: '#7eb8f7' }}>
                      Privacy Policy
                    </Link>.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  {isStrictRegion ? (
                    <>
                      <Button
                        onClick={acceptAll}
                        className="flex-1 sm:flex-none"
                      >
                        Accept All
                      </Button>
                      <Button
                        onClick={acceptNecessaryOnly}
                        variant="outline"
                        className="flex-1 sm:flex-none"
                      >
                        Necessary Only
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="outline"
                        className="flex-1 sm:flex-none"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Customize
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowBanner(false)}
                        className="flex-1 sm:flex-none"
                      >
                        Got it
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="outline"
                        className="flex-1 sm:flex-none"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBanner(false)}
                className="flex-shrink-0"
                aria-label="Dismiss cookie notice"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {brand.key === 'antigreed' ? 'Cookie Settings' : 'Cookie Preferences'}
            </DialogTitle>
            <DialogDescription>
              {brand.key === 'antigreed'
                ? 'Control how AntiGreed.app tracks corporate healthcare data. These settings help us expose pricing manipulation while respecting your privacy.'
                : 'Choose which types of cookies you want to allow. These settings only apply to this device.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg bg-secondary/20">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold text-foreground">Necessary Cookies</h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-matrix-green/20 text-matrix-green">
                    Always Active
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Essential for the website to function properly. These cookies enable basic features like page
                  navigation and access to secure areas. The website cannot function properly without these cookies.
                </p>
              </div>
              <Switch
                checked={true}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">Analytics Cookies</h4>
                <p className="text-sm text-muted-foreground">
                  {brand.key === 'antigreed'
                    ? 'Track how users expose corporate greed patterns. We analyze which hospitals get investigated most, helping us prioritize our fight against healthcare profiteering.'
                    : 'Help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our site and provide a better user experience.'}
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="flex-shrink-0"
              />
            </div>

            {/* Advertising Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">
                  {brand.key === 'antigreed' ? 'Support Cookies' : 'Advertising Cookies'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {brand.key === 'antigreed'
                    ? 'Help fund our fight against corporate greed through targeted ads. Revenue generated supports our mission to expose healthcare profiteering and protect patients from price manipulation.'
                    : 'Used to deliver personalized advertisements relevant to you and your interests. These cookies track your browsing activity across different websites and may be used by advertising partners like Google AdSense.'}
                </p>
              </div>
              <Switch
                checked={preferences.advertising}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, advertising: checked })
                }
                className="flex-shrink-0"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveCustomPreferences}>
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Extend window object for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
